import { RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Hand, Loader2, Monitor, MousePointer2, RotateCw, Smartphone } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PreviewScreen } from './useLayoutPreview';

export type PreviewDevice = 'desktop' | 'mobile';

const DEVICES: Record<PreviewDevice, { w: number; h: number }> = {
    desktop: { w: 1440, h: 900 },
    mobile: { w: 390, h: 844 },
};

/**
 * The vendor's actual visualizer, inside a desktop window or a phone, scaled
 * to fit the canvas. In "Arrange" mode its pieces are outlined and can be
 * dragged; in "Try" mode it behaves exactly as a shopper would see it.
 */
export function LayoutPreview({
    src,
    iframeRef,
    reloadKey,
    ready,
    device,
    onDeviceChange,
    screen,
    onScreenChange,
    edit,
    onEditChange,
    onReload,
}: {
    src: string;
    iframeRef: RefObject<HTMLIFrameElement>;
    reloadKey: number;
    ready: boolean;
    device: PreviewDevice;
    onDeviceChange: (d: PreviewDevice) => void;
    screen: PreviewScreen;
    onScreenChange: (s: PreviewScreen) => void;
    edit: boolean;
    onEditChange: (e: boolean) => void;
    onReload: () => void;
}) {
    const { t } = useTranslation();
    const boxRef = useRef<HTMLDivElement>(null);
    const [box, setBox] = useState({ w: 860, h: 640 });

    useEffect(() => {
        const el = boxRef.current;
        if (!el) return;
        const measure = () => setBox({ w: el.clientWidth, h: Math.max(420, window.innerHeight - 230) });
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        window.addEventListener('resize', measure);
        return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
    }, []);

    const dims = DEVICES[device];
    const chrome = device === 'desktop' ? 30 : 0;
    const bezel = device === 'mobile' ? 12 : 0;
    const scale = Math.min(1, (box.w - bezel * 2) / dims.w, (box.h - chrome - bezel * 2) / dims.h);
    const frameW = Math.round(dims.w * scale);
    const frameH = Math.round(dims.h * scale);

    return (
        <div className="flex w-full flex-col gap-3">
            {/* toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <ToggleGroup type="single" variant="outline" size="sm" value={screen} onValueChange={(v) => v && onScreenChange(v as PreviewScreen)}>
                    <ToggleGroupItem value="gallery">{t('VISUALIZER_LAYOUT.SCREEN.GALLERY')}</ToggleGroupItem>
                    <ToggleGroupItem value="room">{t('VISUALIZER_LAYOUT.SCREEN.ROOM')}</ToggleGroupItem>
                </ToggleGroup>
                <div className="flex items-center gap-2">
                    <ToggleGroup type="single" variant="outline" size="sm" value={edit ? 'arrange' : 'try'} onValueChange={(v) => v && onEditChange(v === 'arrange')}>
                        <ToggleGroupItem value="arrange" aria-label={t('VISUALIZER_LAYOUT.MODE.ARRANGE')}>
                            <Hand className="size-3.5" />
                            {t('VISUALIZER_LAYOUT.MODE.ARRANGE')}
                        </ToggleGroupItem>
                        <ToggleGroupItem value="try" aria-label={t('VISUALIZER_LAYOUT.MODE.TRY')}>
                            <MousePointer2 className="size-3.5" />
                            {t('VISUALIZER_LAYOUT.MODE.TRY')}
                        </ToggleGroupItem>
                    </ToggleGroup>
                    <ToggleGroup type="single" variant="outline" size="sm" value={device} onValueChange={(v) => v && onDeviceChange(v as PreviewDevice)}>
                        <ToggleGroupItem value="desktop" aria-label={t('VISUALIZER_LAYOUT.DEVICE.DESKTOP')}>
                            <Monitor className="size-3.5" />
                        </ToggleGroupItem>
                        <ToggleGroupItem value="mobile" aria-label={t('VISUALIZER_LAYOUT.DEVICE.MOBILE')}>
                            <Smartphone className="size-3.5" />
                        </ToggleGroupItem>
                    </ToggleGroup>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button variant="outline" size="icon-sm" onClick={onReload} aria-label={t('VISUALIZER_LAYOUT.PREVIEW.RELOAD')}>
                                <RotateCw className="size-3.5" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('VISUALIZER_LAYOUT.PREVIEW.RELOAD')}</TooltipContent>
                    </Tooltip>
                    {src && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="outline" size="icon-sm" asChild aria-label={t('VISUALIZER_LAYOUT.PREVIEW.OPEN')}>
                                    <a href={src.replace(/[?&]layoutPreview=1/, '').replace(/[?&]screen=\w+/, '')} target="_blank" rel="noopener noreferrer">
                                        <ExternalLink className="size-3.5" />
                                    </a>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t('VISUALIZER_LAYOUT.PREVIEW.OPEN')}</TooltipContent>
                        </Tooltip>
                    )}
                </div>
            </div>

            {/* device */}
            <div ref={boxRef} className="flex w-full justify-center">
                <div
                    className={cn(
                        'relative overflow-hidden bg-background shadow-[0_12px_40px_rgba(15,15,20,0.12)] ring-1 ring-foreground/10',
                        device === 'desktop' ? 'rounded-xl' : 'rounded-[34px] bg-foreground p-3',
                    )}
                    style={{ width: frameW + bezel * 2, height: frameH + chrome + bezel * 2 }}
                >
                    {device === 'desktop' && (
                        <div className="flex h-[30px] items-center gap-1.5 border-b bg-muted/60 px-3">
                            <span className="size-2.5 rounded-full bg-foreground/15" />
                            <span className="size-2.5 rounded-full bg-foreground/15" />
                            <span className="size-2.5 rounded-full bg-foreground/15" />
                            <span className="ms-3 h-4 flex-1 truncate rounded bg-background px-2 text-[10px] leading-4 text-muted-foreground">
                                {src ? new URL(src).host : ''}
                            </span>
                        </div>
                    )}
                    <div className={cn('relative overflow-hidden', device === 'mobile' && 'rounded-[24px]')} style={{ width: frameW, height: frameH }}>
                        {src ? (
                            <iframe
                                key={`${reloadKey}`}
                                ref={iframeRef}
                                src={src}
                                title={t('VISUALIZER_LAYOUT.PREVIEW.TITLE')}
                                className="absolute left-0 top-0 origin-top-left border-0 bg-white"
                                style={{ width: dims.w, height: dims.h, transform: `scale(${scale})` }}
                            />
                        ) : (
                            <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
                                {t('VISUALIZER_LAYOUT.PREVIEW.UNAVAILABLE')}
                            </div>
                        )}
                        {src && !ready && (
                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/70">
                                <Loader2 className="size-5 animate-spin text-muted-foreground" />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <p className="text-center text-xs text-muted-foreground">
                {edit ? t('VISUALIZER_LAYOUT.PREVIEW.ARRANGE_HINT') : t('VISUALIZER_LAYOUT.PREVIEW.TRY_HINT')}
            </p>
        </div>
    );
}
